// ============================================
// DATA — OurFathersOurSons LLC
// ============================================

import sharhondaPhoto from "../assets/sharhonda.jpeg";

export const NAV_LINKS = [
  { label: "Services", id: "services" },
  { label: "Research", id: "research" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

export const SERVICES = [
  {
    id: "workshops",
    title: "Workshops & Trainings",
    desc: "Interactive sessions for agencies, organizations, and community groups on reentry success, supervision strategies, family support, and reducing recidivism—grounded in research and real field experience.",
    iconKey: "workshops",
  },
  {
    id: "evaluation",
    title: "Program Evaluation",
    desc: "We assess what's working, what's not, and why. You receive clear outcome tracking, stakeholder feedback, and actionable recommendations to strengthen impact and funding readiness.",
    iconKey: "evaluation",
  },
  {
    id: "research",
    title: "Research & Data Analysis",
    desc: "From survey design to database analysis, we help you turn information into decisions. This includes recidivism-related analysis, needs assessments, and reporting for grants or leadership.",
    iconKey: "research",
  },

  {
    id: "speaking",
    title: "Speaking Engagements",
    desc: "Keynotes and conference talks that bridge scholarship with on-the-ground insight—focused on family structure, supervision, and community safety.",
    iconKey: "speaking",
  },
];

export const HOW_STEPS = [
  {
    label: "Book",
    iconKey: "calendar",
    desc: "Choose your service and preferred date",
  },
  {
    label: "Confirm",
    iconKey: "check",
    desc: "Complete intake form and define your goals",
  },
  {
    label: "Deliver",
    iconKey: "rocket",
    desc: "Training, consult, or evaluation is delivered",
  },
  {
    label: "Results",
    iconKey: "chart",
    desc: "Receive your report, tools, and next steps",
  },
];

export const RESEARCH_ACCORDION = [
  {
    title: "Current Research Focus",
    body: "Our ongoing studies examine the intersection of family dynamics and successful reentry, focusing on actionable metrics for community organizations.",
    type: "text",
  },
  {
    title: "Key Topics We Address",
    type: "list",
    items: [
      "Family structure & reentry success",
      "Visitation and social support during incarceration",
      "Supervision success factors and community safety",
      "Housing and living situation stability",
      "Employment stability and economic reintegration",
      "Program effectiveness measurement and evaluation",
    ],
  },
  {
    title: "Publications & Presentations",
    type: "list",
    items: [
      '2025 — York Chea, S. "Sex Policy & Procedures" in the Elgar Encyclopedia of Sex and Society, edited by Dennis Waskul and David Wahl. Edward Elgar Publishing Ltd. [Publication awaiting print]',
      "Forthcoming peer-reviewed research on family structure and recidivism outcomes",
    ],
  },
  {
    title: "Data & Methods Expertise",
    type: "list",
    items: [
      "Mixed methods research design",
      "Quantitative analysis (SPSS, R, Stata)",
      "Qualitative narrative and thematic analysis",
      "Program evaluation methodology",
      "Survey design and community needs assessments",
    ],
  },
];

export const TEAM = [
  {
    id: "sharhonda",
    name: "Dr. Sharhonda York Chea",
    title: "Founder · Researcher · Practitioner",
    photo: sharhondaPhoto,
    para1:
      "Dr. Sharhonda York Chea is a community corrections professional, researcher, and emerging scholar specializing in family structure, social bonds, and recidivism. With over a decade of experience in community supervision, she currently serves as an Intensive Supervised Release (ISR) parole agent in Ramsey County, Minnesota, where she works directly with high-risk individuals transitioning from incarceration back into the community. Dr. York Chea earned her Ph.D. in Sociology from Iowa State University, where her research focused on how family structure and social support systems influence recidivism outcomes among individuals on parole. Her work is grounded in Social Control Theory and integrates both quantitative analysis and real-world practitioner insight.",
    para2:
      "In addition to her role in corrections, Dr. York Chea is actively engaged in community-based work. She serves as a domestic violence group facilitator and co-facilitates family-focused programming through Rebound Inc., and has worked with youth as a Youth Advisory Board coordinator. She is the founder of Our Fathers Our Sons, a consulting and training organization dedicated to bridging the gap between research and practice—equipping agencies and communities with evidence-based strategies to support justice-involved individuals and their families. Dr. York Chea is also an active member of Zeta Phi Beta Sorority, Incorporated, serving in leadership at the state level, and aims to expand her impact through teaching, public speaking, and consulting.",
    credentials: [
      "Ph.D. in Sociology, Iowa State University — research focused on family structure, social bonds, and recidivism",
      "Intensive Supervised Release (ISR) Parole Agent, Ramsey County, Minnesota — over a decade in community supervision",
      "Domestic violence group facilitator and co-facilitator of family-focused programming through Rebound Inc.",

      "State-level leader, Zeta Phi Beta Sorority, Incorporated",
    ],
    bioFull: [
      "Dr. Sharhonda York Chea is a community corrections professional, researcher, and emerging scholar specializing in family structure, social bonds, and recidivism. With over a decade of experience in community supervision, she currently serves as an Intensive Supervised Release (ISR) parole agent in Ramsey County, Minnesota, where she works directly with high-risk individuals transitioning from incarceration back into the community.",
      "Dr. York Chea earned her Ph.D. in Sociology from Iowa State University, where her research focused on how family structure and social support systems influence recidivism outcomes among individuals on parole. Her work is grounded in Social Control Theory and integrates both quantitative analysis and real-world practitioner insight. Using administrative data from Ramsey County, her research highlights the critical role of family connections and employment in reducing reoffending.",
      "In addition to her role in corrections, Dr. York Chea is actively engaged in community-based work. She serves as a domestic violence group facilitator and co-facilitates family-focused programming through Rebound Inc. She also worked with youth as the Youth Advisory Board Coordinator. Her work centers on strengthening family systems, promoting accountability, and supporting successful reentry.",
      "Dr. York Chea is the founder of Our Fathers Our Sons, a consulting and training organization dedicated to bridging the gap between research and practice. Through workshops, speaking engagements, and program development, she equips agencies and communities with evidence-based strategies to support justice-involved individuals and their families.",
      "She is also an active member of Zeta Phi Beta Sorority, Incorporated, where she serves in leadership at the state level. Passionate about education and mentorship, Dr. York Chea aims to expand her impact through teaching, public speaking, and consulting, bringing a unique voice that blends lived professional experience with rigorous academic research.",
    ],
    pastWork: [
      "Conducted research using Ramsey County administrative data highlighting the role of family connections and employment in reducing reoffending",
      "Co-facilitated family-focused reentry programming through Rebound Inc.",
      "Coordinated youth programming as Youth Advisory Board coordinator",
      "Founded Our Fathers Our Sons LLC — delivering workshops, speaking engagements, and evidence-based program development",
    ],
    publications: [
      '2025 — York Chea, S. "Sex Policy & Procedures" in the Elgar Encyclopedia of Sex and Society, edited by Dennis Waskul and David Wahl. Edward Elgar Publishing Ltd. [Publication awaiting print]',
    ],
    manuscripts: [
      "York Chea, S., Larter, C., and Harper, S., Analyzing Gendered Differences in Homicide Cases: A Longitudinal Investigation of Offender Arrest Histories in Chicago. [In preparation to be submitted to Criminology, Fall 2026]",
    ],
    researchExperience: [
      {
        years: "2025 - Present",
        title: "Family Structure and Its Impact on Recidivism",
      },
      {
        years: "2025 - Present",
        title:
          "Analyzing Gendered Differences in Homicide Cases: A Longitudinal Investigation of Offender Arrest Histories in Chicago",
      },
    ],
  },
];

export const PRICING = [
  {
    tier: "Basic",
    title: "Intro Call",
    price: "Free",
    timeline: "30 mins",
    highlight: false,
    features: [
      "Brief discovery call",
      "Discuss your goals and needs",
      "Learn how we can help",
      "No obligation",
    ],
  },
  {
    tier: "Starter",
    title: "Consultation",
    price: "$250",
    timeline: "One hour consultation",
    highlight: false,
    features: [
      "Initial needs assessment",
      "Strategy session (1:1 or team)",
      "Written summary + recommendations",
      "Follow-up Q&A session (30 min)",
    ],
  },
  {
    tier: "Standard",
    title: "Workshop / Training Package",
    price: "$2,500",
    timeline: "1 day full day training",
    rate: "$1,250 half day training",
    highlight: true,
    features: ["Custom curriculum design", "On-site or virtual delivery"],
  },
  {
    tier: "Advanced",
    title: "Evaluation + Research",
    price: "$5,000",
    timeline: "4–8 weeks",
    rate: "$175/hr",
    highlight: false,
    features: [
      "Full program evaluation design",
      "Mixed-methods data analysis",

      "Final report + leadership presentation",
    ],
  },
];
